import { HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

/**
 * Transient-failure retry interceptor.
 *
 * The remote-dev setup proxies the API to a ClickHouse instance over an SSH
 * tunnel. When the tunnel hiccups, every CH-backed endpoint returns 5xx at
 * once — transient, so a short jittered backoff recovers without surfacing an
 * error to the user.
 *
 * IMPORTANT: 429 is NOT retried here. Retrying a rate-limited request adds load
 * to an endpoint that just told us to slow down — with the 2s tickers/live poll
 * that turned into a self-sustaining retry storm (each poll fired 1+3 requests,
 * the retries counted against the limit → more 429 → console spam). Back-off for
 * 429 is owned by the caller (socket poller) + the rate-limit interceptor; here
 * we only retry genuine transient failures (network / gateway / CH 5xx).
 *
 * Only safe (idempotent) GET requests are retried, so we never replay a POST
 * that may have already mutated state.
 */
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 400;
const MAX_DELAY_MS = 4_000;

/** Status codes worth retrying: gateway/CH transient errors + network. NOT 429. */
const RETRYABLE_STATUSES = new Set([0, 500, 502, 503, 504]);

export const retryInterceptor: HttpInterceptorFn = (req, next): Observable<HttpEvent<unknown>> => {
	if (req.method !== 'GET') {
		return next(req);
	}

	return next(req).pipe(
		retry({
			count: MAX_RETRIES,
			delay: (error: unknown, retryCount: number) => {
				if (
					!(error instanceof HttpErrorResponse) ||
					!RETRYABLE_STATUSES.has(error.status)
				) {
					return throwError(() => error);
				}

				// Honor server-provided Retry-After (seconds) when present, else
				// exponential backoff with jitter, capped at MAX_DELAY_MS.
				const retryAfter = Number(error.headers?.get('retry-after'));
				const serverDelay =
					Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 0;
				const backoff = Math.min(BASE_DELAY_MS * 2 ** (retryCount - 1), MAX_DELAY_MS);
				const jitter = Math.random() * BASE_DELAY_MS;
				const wait = Math.max(serverDelay, backoff + jitter);

				return timer(wait);
			},
		}),
	);
};
